import { List, Box, CircularProgress, Typography } from "@mui/material";
import { ReactNode, useEffect } from "react";
import { useInView } from "react-intersection-observer";

interface ItemWithId {
  id: string | number;
  [key: string]: any; // Allows for any other properties
}

// Props for the reusable Infinite Scroll component
interface InfiniteScrollListProps<T extends object> {
  items: T[];
  renderItem: (item: T, index: number) => ReactNode; // Function to render each item
  loadMore: () => void; // Callback to fetch the next page of data
  hasMore: boolean; // Tells the component if more data can be fetched
  loading: boolean; // Tells the component if data is currently being fetched
}

/**
 * Handles the rendering of the list and the Intersection Observer logic.
 * @param props - items, renderItem function, loadMore callback, hasMore/loading states.
 */
export const InfiniteScrollList = <T extends object>({
  items,
  renderItem,
  loadMore,
  hasMore,
  loading,
}: InfiniteScrollListProps<T>) => {
  // Setup Intersection Observer
  const { ref, inView } = useInView({
    rootMargin: "200px 0px", // Trigger load when 200px from the bottom
  });

  // Trigger loadMore callback when the sentinel element is in view and we need more data
  useEffect(() => {
    // Only call loadMore if the sentinel is visible AND we have more data AND we are not already loading
    if (inView && hasMore && !loading) {
      loadMore();
    }
  }, [inView, hasMore, loading, loadMore]);

  return (
    <>
      {/* Render the actual items using the passed renderItem function */}
      {items.map(renderItem)}

      {/* --- Scroll Trigger / Sentinel Element --- */}
      {/* This Box is the element the Intersection Observer watches. */}
      <Box ref={ref} sx={{ py: 3, textAlign: "center" }}>
        {loading && hasMore && (
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
            }}
          >
            <CircularProgress size={30} color="primary" sx={{ mb: 1 }} />
            <Typography variant="body2" color="text.secondary">
              Loading more items...
            </Typography>
          </Box>
        )}

        {!hasMore && items.length > 0 && (
          <Typography
            variant="body2"
            color="success.main"
            sx={{ fontWeight: 500 }}
          >
            All {items.length > 1 && items.length} items loaded.
          </Typography>
        )}
      </Box>
    </>
  );
};
